import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import type { LiveUsersServiceInterface } from '@lib/live-tracking';

import { FsMapLiveUsersServiceProvider, IFsMap } from '@lib/geo/core';
import { SnLiveUser } from '@lib/live-tracking/models';

/**
 * Facade for map live users that delegates to an injected provider.
 */
@Injectable()
export class MapLiveUsersFacadeService<
  TMap extends IFsMap,
  TComponentMarker,
> extends FsMapLiveUsersServiceProvider<TMap, TComponentMarker> {
  constructor(
    private readonly provider: FsMapLiveUsersServiceProvider<TMap, TComponentMarker>,
    private readonly businessService: LiveUsersServiceInterface,
  ) {
    super();
  }

  // Re-implemented getters - delegate business logic to business service
  public get isAvailable(): boolean {
    return this.businessService.isAvailable;
  }

  public get userList(): SnLiveUser[] {
    return this.businessService.userList;
  }

  public get countUsers(): number {
    return this.businessService.countUsers;
  }

  public get countActiveUser(): number {
    return this.businessService.countActiveUser;
  }

  public get selected(): SnLiveUser {
    return this.provider.selected;
  }

  // Re-implemented methods required by IInnerLayer
  public addToMap(): void {
    return this.provider.addToMap();
  }

  public removeFromMap(): void {
    return this.provider.removeFromMap();
  }

  // Re-implemented core methods
  public onMapLoaded(map: TMap): void {
    return this.provider.onMapLoaded(map);
  }

  public isSelected(id: string): boolean {
    return this.provider.isSelected(id);
  }

  public select(id: string): void {
    return this.provider.select(id);
  }

  public deselect(): void {
    return this.provider.deselect();
  }

  // Business methods delegate to business service (not provider anymore)
  public teamIsActive(id: string): boolean {
    return this.businessService.teamIsActive(id);
  }

  public toggleTeam(id: string): void {
    return this.businessService.toggleTeam(id);
  }

  // Observable passthroughs - delegate to business service
  public get activeUserList$(): Observable<SnLiveUser[]> {
    return this.businessService.activeUserList$;
  }

  public get disabledTeams$(): Observable<Set<string>> {
    return this.businessService.disabledTeams$;
  }

  public get countUsers$(): Observable<number> {
    return this.businessService.countUsers$;
  }

  public get countActiveUser$(): Observable<number> {
    return this.businessService.countActiveUser$;
  }

  public createUserMarker(user: SnLiveUser): TComponentMarker {
    return this.provider.createUserMarker(user);
  }

  public userIsOffShift(userId: string): Observable<void> {
    return this.businessService.userIsOffShift(userId);
  }
}
